/**
 * PersonMembershipPane - the teams and departments this person belongs to.
 *
 * THE ACCESS PANE'S SLOT, MIRRORED. On a team or a project the right-hand
 * roster answers "who is on this record", and `AccessPane` renders it. A
 * person is not a record people are on - they ARE the person - so the same
 * dock, the same rail position and the same anatomy (a row per group, a count
 * and a way onward in the footer) answer the mirror question instead.
 *
 * It fetches BOTH ORG LISTS because there is no per-person membership
 * endpoint; see `personMembership`. That is two requests, made only while the
 * pane is on screen - the page passes this body as a THUNK, so a person
 * opened and read from their Matters tab never makes them at all.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { PiBuildingsLight, PiUsersThreeLight } from 'react-icons/pi';
import { useAppDispatch } from '../../../../store';
import { fetchAllTeams } from '../../../../store/actions/organization/teamActions';
import { fetchAllDepartments } from '../../../../store/actions/organization/DepartmentActions';
import Badge from '../../../shared/TailwindComponents/Badge';
import Skeleton from '../../../shared/TailwindComponents/Skeleton';
import EmptyState from '../../../shared/TailwindComponents/EmptyState';
import { ContextSection } from '../../../shared/brand/ContextPane';
import PaneFooter from '../../../shared/surface/PaneFooter';
import { OrgGroup, PersonIdentity, groupsFor, roleIn } from './personMembership';

interface Props {
  person: PersonIdentity;
  /** The person these memberships belong to - used by the empty line. */
  displayName: string;
}

const GroupRow: React.FC<{
  group: OrgGroup;
  role: string;
  icon: React.ReactNode;
  onClick: () => void;
}> = ({ group, role, icon, onClick }) => {
  const { t } = useTranslation();
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid="person-membership-row"
        className="w-full flex items-center gap-2.5 rounded-lg px-1.5 py-1.5 text-start
                   transition-colors hover:bg-surface-sunken/70"
      >
        <span className="[&>svg]:h-4 [&>svg]:w-4 text-fg-muted flex-shrink-0">{icon}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-fg-body truncate">{group.name}</span>
          <span className="block text-[11px] text-fg-muted truncate">
            {group.member_count ?? group.members?.length ?? 0} {t('members')}
          </span>
        </span>
        {/* No badge for a plain member: the backend does not say "member" for
            everyone, and asserting it would invent a fact. */}
        {role === 'lead' && <Badge color="indigo">{t('Lead')}</Badge>}
      </button>
    </li>
  );
};

const PersonMembershipPane: React.FC<Props> = ({ person, displayName }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const [teams, setTeams] = useState<OrgGroup[]>([]);
  const [departments, setDepartments] = useState<OrgGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const [allTeams, allDepartments] = await Promise.all([
          dispatch(fetchAllTeams() as any),
          dispatch(fetchAllDepartments() as any),
        ]);
        if (cancelled) return;
        setTeams(groupsFor(allTeams as OrgGroup[], person));
        setDepartments(groupsFor(allDepartments as OrgGroup[], person));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dispatch, person.uuid, person.email]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    // Content-shaped, not a spinner: the pane has a known silhouette.
    return <Skeleton variant="pane" rows={3} />;
  }

  const total = teams.length + departments.length;

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="person-membership-pane">
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main px-3 py-2 space-y-3">
        {total === 0 && (
          <EmptyState
            density="compact"
            icon={<PiUsersThreeLight />}
            headline={t('Not a member of any team or department yet')}
            subtext={displayName}
          />
        )}

        {departments.length > 0 && (
          <div>
            <ContextSection>{t('Departments')}</ContextSection>
            <ul className="space-y-1">
              {departments.map((group) => (
                <GroupRow
                  key={group.id}
                  group={group}
                  role={roleIn(group, person)}
                  icon={<PiBuildingsLight />}
                  onClick={() => history.push(`/departments/${group.id}`)}
                />
              ))}
            </ul>
          </div>
        )}

        {teams.length > 0 && (
          <div>
            <ContextSection>{t('Teams')}</ContextSection>
            <ul className="space-y-1">
              {teams.map((group) => (
                <GroupRow
                  key={group.id}
                  group={group}
                  role={roleIn(group, person)}
                  icon={<PiUsersThreeLight />}
                  onClick={() => history.push(`/teams/${group.id}`)}
                />
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* The shared pane footer - stat left, action right - rather than the
          hand-rolled `border-t` + `Well` line this had, which was the same
          shape drawn a second time. */}
      <PaneFooter count={total} label={total === 1 ? t('membership') : t('memberships')} />
    </div>
  );
};

export default PersonMembershipPane;
